import React, { useState } from "react";
import { toast } from "react-toastify";
import { SettingsCard } from "../SettingsCard";
import { SettingsAPI } from "../../../services/settings.service";
import { ApiError } from "../../../services/api.errors";
import { exportBackup, REPORT_RETENTION_DAYS } from "../../../utils/exportToExcel";
import { logger } from "../../../utils/logger";
import { useRole } from "../../../hooks/useRole";
import { actionErrorMessage } from "../../../utils/network";

// NOTE: The Theme & UI, Security, and Notifications cards are intentionally
// hidden for now (not shown to any user). Theme & UI and Security were UI-only
// stubs; Notifications is backend-wired but parked until it's needed again.
// Their implementations live in git history — re-add the cards here to restore.

interface SystemPreferencesProps {
    /** Current user's role, threaded from the Settings page (which resolves it via
     *  useRole). Only the LEGACY fallback for `data.export` now — see canExport
     *  below. `null`/empty while the role is still loading → treated as non-admin,
     *  so the export stays disabled until we know who the caller is. */
    userRole: string | null;
}

export const SystemPreferences: React.FC<SystemPreferencesProps> = ({ userRole }) => {
    const isAdmin = userRole === "Admin";
    // Exporting is gated on the data.export GRANT, not on being an Admin. Those
    // came apart the moment "Export Clinic Data" became a revocable checkbox: an
    // Admin with it unticked is refused, so telling them "only clinic admins can
    // export" is both wrong and unactionable. Legacy fallback to the role keeps a
    // pre-RBAC session behaving as before.
    const { canOr } = useRole();
    const canExport = canOr("data.export", isAdmin);
    const [downloadingBackup, setDownloadingBackup] = useState(false);
    // Ephemeral UI state only — intentionally not persisted, so it resets to
    // "No backups yet" on reload. There is no server-side "last backup" record.
    const [backup, setBackup] = useState<{ last_backup: string | null }>({ last_backup: null });

    const handleDownloadBackup = async () => {
        if (!canExport) {
            toast.error("You don't have the \"Export Clinic Data\" permission. Ask an admin to grant it.");
            return;
        }
        setDownloadingBackup(true);
        try {
            // Authorize + audit server-side BEFORE producing the file. The backup
            // Excel is built entirely client-side from the offline DB and never
            // reaches the backend, so this call is the only server-side record of
            // who exported the clinic's PHI. Fail closed: if it doesn't return 200,
            // no file is written.
            await SettingsAPI.recordBackupExport(REPORT_RETENTION_DAYS);
            const result = await exportBackup();
            if (result.success) {
                toast.success(`Backup downloaded: ${result.filename}`);
                setBackup(prev => ({ ...prev, last_backup: new Date().toLocaleString() }));
            } else if (result.reason === "no_activity") {
                toast.info(`No data in the last ${REPORT_RETENTION_DAYS} days to back up`);
            } else {
                toast.error("Download failed");
            }
        } catch (err) {
            // PHI-safe logger contract: pass only the Error (the logger records its
            // name), never the offline record data that a decrypt error might carry.
            logger.error(
                "SystemPreferences.handleDownloadBackup failed",
                err instanceof Error ? err : undefined,
            );
            // Fail closed either way (no file). Give an honest reason per status:
            // 403 = the grant is missing (actionErrorMessage relays the server's
            // reason, which names the permission), 401 = the session (not the
            // network) is the problem, anything else = a real transport failure.
            let message: string;
            if (err instanceof ApiError && err.isForbidden) {
                message = actionErrorMessage(err, "You don't have permission to export a backup.");
            } else if (err instanceof ApiError && err.isUnauthorized) {
                message = "Your session has expired. Please sign in again, then retry.";
            } else {
                message = "Couldn't record this export for the audit log. Check your connection and try again.";
            }
            toast.error(message);
        } finally {
            setDownloadingBackup(false);
        }
    };

    return (
        <div className="space-y-5">

            {/* ── Backup & Restore ─────────────────────────────────────── */}
            <SettingsCard
                title="Backup & Restore"
                subtitle="Download the last 30 days of clinic data as an Excel file"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <ellipse cx="12" cy="5" rx="9" ry="3"/>
                        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                    </svg>
                }
            >
                <div className="space-y-4">
                    {/* Last Backup info — ephemeral; resets on reload. */}
                    <div className="rounded-xl bg-cyan-50 px-4 py-3.5">
                        <p className="text-xs font-medium text-gray-500">Last Backup</p>
                        <p className="text-sm font-medium text-gray-800 mt-0.5">
                            {backup.last_backup ?? "No backups yet"}
                        </p>
                    </div>

                    {/* Download action — needs data.export; recordBackupExport
                        re-checks it server-side and fails closed. */}
                    <button
                        onClick={handleDownloadBackup}
                        disabled={downloadingBackup || !canExport}
                        className="w-full py-2.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {downloadingBackup ? "Downloading…" : "Download Backup"}
                    </button>

                    {!canExport && (
                        <p className="text-xs text-gray-400">
                            Exporting a clinic backup needs the &quot;Export Clinic Data&quot;
                            permission. Ask an admin to grant it.
                        </p>
                    )}
                </div>
            </SettingsCard>

        </div>
    );
};
